import { ArrowLeft, CheckCircle2, ChevronDown, ChevronRight, FileText, ImagePlus, Link2, MapPin, Plus, Search, Trash2, X } from 'lucide-react';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { apiRequest, apiUploadFile, ApiError } from '../../api/http';
import { navigate } from '../../app/router';
import { useAuth } from '../../auth/AuthContext';
import { Field, Spinner } from '../../shared/components';
import { invalidateMerchantOverview, MERCHANT_CLAIM_FLASH_KEY } from './MerchantDashboardPage';
import './merchant-claim.css';

// "เคลมร้านเพิ่ม" for a signed-in merchant, matching the app's claim page:
// search + pick the place, store photos (required), reference links and
// documents (optional), then back to /for-merchant/manage.
// /for-merchant/join stays the public sign-up link.

type Place = { id: string; name: string; address: string; image: string };
type Evidence = { name: string; key: string; r2_key: string; url: string; type: string; content_type: string; mime_type: string };
const errorText = (reason: unknown) => reason instanceof ApiError || reason instanceof Error ? reason.message : 'ทำรายการไม่สำเร็จ กรุณาลองใหม่';
const LINKS = [['facebook', 'Facebook'], ['instagram', 'Instagram'], ['google', 'Google Business Profile']] as const;

function toPlace(item: Record<string, unknown>): Place {
  return {
    id: String(item.id || item._id || item.place_id || ''),
    name: String(item.name || item.place_name || ''),
    image: String(item.image_url || item.photo_url || item.logo_url || ''),
    address: [item.district, item.city].map(value => String(value || '').trim()).filter(Boolean).join(' · ') || String(item.address || ''),
  };
}

function EvidenceList({ items, empty, busy, onRemove }: { items: Evidence[]; empty: string; busy: boolean; onRemove: (index: number) => void }) {
  if (!items.length) return <p className="mcp-empty">{empty}</p>;
  return <ul className="mcp-files">{items.map((item, index) => <li key={item.key}>
    {item.content_type.startsWith('image/') && item.url ? <img src={item.url} alt="" /> : <span className="mcp-files__icon"><FileText size={18} /></span>}
    <span>{item.name}</span>
    <button type="button" className="button button--ghost" aria-label={`นำ ${item.name} ออก`} disabled={busy} onClick={() => onRemove(index)}><Trash2 size={16} /></button>
  </li>)}</ul>;
}

export function MerchantClaimPage() {
  const { session, restoring } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Place[]>([]);
  const [searching, setSearching] = useState(false);
  const [place, setPlace] = useState<Place | null>(null);
  const [photos, setPhotos] = useState<Evidence[]>([]);
  const [documents, setDocuments] = useState<Evidence[]>([]);
  const [links, setLinks] = useState({ facebook: '', instagram: '', google: '' });
  const [showLinks, setShowLinks] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  useEffect(() => { if (session && dialogRef.current && !dialogRef.current.open) dialogRef.current.showModal(); }, [session]);
  const photoInput = useRef<HTMLInputElement | null>(null);
  const [editingPlace, setEditingPlace] = useState(false);
  const searchAbort = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!restoring && !session) navigate(`/login?next=${encodeURIComponent('/for-merchant/claim')}`, true);
  }, [restoring, session]);

  useEffect(() => {
    const text = query.trim();
    if (!text || text === place?.name) { setResults([]); return; }
    const timer = window.setTimeout(() => {
      searchAbort.current?.abort();
      const controller = new AbortController(); searchAbort.current = controller;
      setSearching(true);
      apiRequest<{ items: Record<string, unknown>[] }>('/cafes', { query: { q: text, page_size: 8 }, signal: controller.signal })
        .then(data => { if (!controller.signal.aborted) setResults((data.items || []).map(toPlace).filter(item => item.id && item.name)); })
        .catch(reason => { if (!controller.signal.aborted) setError(errorText(reason)); })
        .finally(() => { if (!controller.signal.aborted) setSearching(false); });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query, place?.name]);

  const back = () => { if (history.length > 1) history.back(); else navigate('/for-merchant/manage'); };
  const choose = (item: Place) => { setPlace(item); setEditingPlace(false); setQuery(item.name); setResults([]); setError(''); };

  const upload = async (files: File[], storePhoto: boolean) => {
    if (!files.length) return;
    setBusy(true); setError('');
    try {
      for (const file of files) {
        const uploaded = await apiUploadFile(file, 'merchant-evidence');
        const url = uploaded.public_url || uploaded.signed_read_url || '';
        if (!uploaded.key || !url) throw new Error('อัปโหลดไม่สำเร็จ กรุณาลองใหม่');
        const contentType = file.type || 'application/octet-stream';
        const evidence = { name: file.name, key: uploaded.key, r2_key: uploaded.key, url, content_type: contentType, mime_type: contentType, type: storePhoto ? 'storefront' : 'business_registration' };
        (storePhoto ? setPhotos : setDocuments)(previous => [...previous, evidence]);
      }
    } catch (reason) { setError(`อัปโหลดไฟล์ไม่สำเร็จ: ${errorText(reason)}`); } finally { setBusy(false); }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !session) return;
    if (!place) { setError('กรุณาค้นหาและเลือกร้านจากผลการค้นหาก่อนส่งคำขอ'); return; }
    if (!photos.length) { setError('กรุณาเพิ่มภาพถ่ายร้านก่อนส่งคำขอ'); return; }
    setBusy(true); setError('');
    try {
      const note = LINKS.filter(([key]) => links[key].trim()).map(([key, label]) => `${label}: ${links[key].trim()}`).join('\n');
      await apiRequest('/merchant/applications', { method: 'POST', body: {
        user_id: session.expert_id, place_id: place.id, business_name: place.name,
        contact_name: session.display_name || '', contact_email: session.email || '', contact_phone: session.tel || '',
        documents, store_photos: photos, note,
      } });
      invalidateMerchantOverview();
      try { sessionStorage.setItem(MERCHANT_CLAIM_FLASH_KEY, `ส่งคำขอเคลม ${place.name} แล้ว รอทีมงานตรวจสอบ`); } catch { /* flash is optional */ }
      navigate('/for-merchant/manage');
    } catch (reason) { setError(errorText(reason)); } finally { setBusy(false); }
  };

  if (!session) return <main className="boot"><Spinner /></main>;
  return <dialog ref={dialogRef} className="mcp-dialog" aria-labelledby="mcp-title" onCancel={event => { event.preventDefault(); back(); }}>
    <form className="mcp" onSubmit={submit} noValidate>
      <header className="mcp-dialog-header"><div className="mcp-dialog-title"><button type="button" className="mcp-icon-button" aria-label="ย้อนกลับ" onClick={back}><ArrowLeft size={22}/></button><h1 id="mcp-title">เคลมร้านเพิ่ม</h1><button type="button" className="mcp-icon-button" aria-label="ปิด" onClick={back}><X size={21}/></button></div><p className="mcp-hint">ค้นหาและเลือกร้านที่เป็นเจ้าของ แล้วเพิ่มภาพถ่ายร้านอย่างน้อย 1 ภาพ</p></header>
      <section className="panel mcp-section">
        
        {place && !editingPlace ? <button type="button" className="mcp-selected" onClick={() => setEditingPlace(true)} disabled={busy}>
          <strong>ร้านที่เลือก</strong><span className="mcp-selected__row"><CheckCircle2 size={26}/>
          {place.image ? <img src={place.image} alt="" onError={event => { event.currentTarget.style.display = 'none'; }} /> : <span className="mcp-place-icon"><MapPin size={26}/></span>}
          <span><b>{place.name}</b><small>{place.id}</small></span><ChevronRight size={22}/></span>
        </button> : <div className="mcp-search">
          <Field label="ค้นหาร้าน">
            <span className="mcp-input-icon"><Search aria-hidden="true" /><input value={query} placeholder="พิมพ์ชื่อร้าน" autoComplete="off" disabled={busy} onChange={event => { setQuery(event.target.value); if (place && event.target.value !== place.name) setPlace(null); }} /></span>
          </Field>
          {searching && <p className="mcp-empty">กำลังค้นหา...</p>}
          {!!results.length && <ul className="mcp-results" role="listbox" aria-label="ผลการค้นหาร้าน">{results.map(item => <li key={item.id}>
            <button type="button" role="option" aria-selected={place?.id === item.id} onClick={() => choose(item)}><MapPin size={18} /><span><strong>{item.name}</strong>{item.address && <small>{item.address}</small>}</span></button>
          </li>)}</ul>}
          {place && <p className="mcp-selected" role="status"><MapPin size={16} />เลือกแล้ว: <b>{place.name}</b></p>}
        </div>}
      </section>

      <section className="panel mcp-section">
        <div className="mcp-head"><div><h2>ภาพถ่ายร้าน</h2><p className="mcp-hint">เพิ่มภาพหน้าร้าน/ภายในร้านได้หลายรูป และลบออกได้</p></div>
          <label className={`button button--ghost mcp-upload${busy ? ' is-disabled' : ''}`}><ImagePlus size={17} />เพิ่มภาพถ่ายร้าน<input ref={photoInput} hidden type="file" multiple accept="image/*" disabled={busy} onChange={event => { const files = Array.from(event.target.files || []); event.target.value = ''; void upload(files, true); }} /></label></div>
        {photos.length ? <div className="mcp-photo-strip">{photos.map((photo, index) => <div className="mcp-photo" key={photo.key}>
          <img src={photo.url} alt={photo.name}/><button type="button" aria-label={`นำ ${photo.name} ออก`} disabled={busy} onClick={() => setPhotos(items => items.filter((_, i) => i !== index))}><X size={18}/></button>
        </div>)}<button type="button" className="mcp-photo-add" disabled={busy} onClick={() => photoInput.current?.click()}><Plus size={25}/><span>เพิ่มรูป</span></button></div> : <p className="mcp-empty">ยังไม่มีภาพถ่ายร้าน</p>}
      </section>

      <section className="panel mcp-section">
        <h2>ช่องทางอ้างอิง (ถ้ามี)</h2><button type="button" className="mcp-toggle" aria-expanded={showLinks} onClick={() => setShowLinks(value => !value)}><Link2 size={23}/><span>เว็บไซต์ / โซเชียลมีเดีย</span><ChevronDown size={18} /></button>
        {showLinks && LINKS.map(([key, label]) => <Field key={key} label={label}><input type="url" placeholder="https://" disabled={busy} value={links[key]} onChange={event => setLinks({ ...links, [key]: event.target.value })} /></Field>)}
      </section>

      <section className="panel mcp-section mcp-documents">
        <h2>เอกสาร (ไม่บังคับ)</h2><div className="mcp-document-box">
        <div className="mcp-head"><div><h3>เอกสารเจ้าของร้าน</h3><p className="mcp-hint">เพิ่มเอกสารทะเบียนร้าน บัตร หรือหลักฐานอื่นได้ถ้ามี รองรับ PDF/JPG/PNG</p></div>
          <label className={`button button--ghost mcp-upload${busy ? ' is-disabled' : ''}`}><FileText size={17} />เพิ่มเอกสาร<input hidden type="file" multiple accept=".png,.jpg,.jpeg,.webp,.heic,.heif,.pdf" disabled={busy} onChange={event => { const files = Array.from(event.target.files || []); event.target.value = ''; void upload(files, false); }} /></label></div>
        <EvidenceList items={documents} empty="ยังไม่มีเอกสาร" busy={busy} onRemove={index => setDocuments(items => items.filter((_, i) => i !== index))} /></div>
      </section>

      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="mcp-submit"><button className="button button--primary" disabled={busy}>{busy ? 'กำลังดำเนินการ...' : 'ส่งคำขอเคลมร้าน'}</button></div>
    </form>
  </dialog>;
}
